import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { copyToClipboard } from "@/lib/clipboard";
import { SmtpCard } from "./SmtpCard";
import { ScreenTips } from "@/components/GuidedHighlights";
import { COACHMARKS } from "@/lib/coachmarks";
import { School, Users, Ticket, Loader2, Plus, Copy, Trash2, Lock, ArrowRight, Mail, GraduationCap, Users2, Send, CheckCircle2, AlertTriangle, FileText } from "lucide-react";
import PurchaseOrderModal from "./PurchaseOrderModal";

const inputClass = "w-full rounded-2xl border-2 border-[#E5DECF] bg-white px-4 py-3 font-body text-[#1F2937] outline-none focus:border-[#0D9488]";

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="rounded-3xl border border-[#E5DECF] bg-white p-5">
      <div className="flex items-center gap-2 text-[#0D9488]"><Icon size={18} /><span className="text-xs uppercase tracking-wide font-semibold text-[#6B7280]">{label}</span></div>
      <div className="mt-1 font-heading text-3xl font-extrabold text-[#1F2937]">{value}</div>
    </div>
  );
}

export default function SchoolAdminHome() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [schoolName, setSchoolName] = useState("");
  const [creating, setCreating] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [mode, setMode] = useState("single"); // "single" | "bulk"
  const [bulkText, setBulkText] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [showPO, setShowPO] = useState(false);

  const load = async () => { try { setData(await api.getMySchool()); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const parsedBulk = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const raw of bulkText.split(/[\s,;\n]+/)) {
      const e = (raw || "").trim().toLowerCase();
      if (!e || !e.includes("@") || seen.has(e)) continue;
      seen.add(e); out.push(e);
    }
    return out;
  }, [bulkText]);

  if (loading) return <div className="p-16 text-center"><Loader2 className="mx-auto animate-spin text-[#0D9488]" /></div>;

  const active = data.subscription?.status === "active";
  const school = data.school;

  const createSchool = async () => {
    if (!schoolName.trim()) { toast.error("Name your school"); return; }
    setCreating(true);
    try { await api.createSchool(schoolName.trim()); toast.success("School created!"); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setCreating(false); }
  };

  const invite = async () => {
    setInviting(true);
    try {
      const r = await api.createInvite({ role: "teacher", email: inviteEmail.trim() || undefined });
      if (r.emailed) toast.success(`Invite emailed to ${r.invite.email}`);
      else await copyToClipboard(r.invite.link, "Invite link created & copied — share it with your teacher");
      setInviteEmail("");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setInviting(false); }
  };

  const revoke = async (id) => { try { await api.revokeInvite(id); toast.success("Seat reclaimed"); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const inviteLink = (token) => `${window.location.origin}/invite/${token}`;
  const copyLink = (token) => copyToClipboard(inviteLink(token), "Invite link copied");

  const runBulk = async () => {
    if (!parsedBulk.length) { toast.error("Paste at least one email"); return; }
    setBulkBusy(true);
    setBulkResult(null);
    try {
      const r = await api.bulkInviteTeachers(parsedBulk);
      setBulkResult(r);
      const okMsg = `${r.created.length} invited${r.skipped.length ? `, ${r.skipped.length} skipped` : ""}`;
      if (r.created.length) toast.success(okMsg);
      else toast.warning(okMsg);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBulkBusy(false); }
  };

  const clearBulk = () => { setBulkText(""); setBulkResult(null); };

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#0D9488]"><School size={14} /> School Admin</div>
      <h1 className="mt-1 font-heading text-3xl font-extrabold text-[#1F2937]">{school ? school.name : "Your School"}</h1>

      {!active && !school && (
        <div className="mt-6 rounded-3xl border-2 border-[#FF9E1B] bg-[#FF9E1B]/10 p-6" data-testid="school-upgrade-gate">
          <div className="flex items-center gap-2 text-[#B45309]"><Lock size={18} /><h2 className="font-heading text-lg font-extrabold">Activate your School plan</h2></div>
          <p className="mt-2 font-body text-sm text-[#6B7280]">Buy the Individual School plan to unlock teacher seats you can invite educators into.</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link to="/pricing" data-testid="school-choose-plan" className="inline-flex items-center gap-2 rounded-full bg-[#FF9E1B] px-6 py-3 font-heading font-bold text-[#1F2937] hover:brightness-95">Choose a plan <ArrowRight size={16} /></Link>
            <button onClick={() => setShowPO(true)} data-testid="school-po-btn" className="inline-flex items-center gap-2 rounded-full border-2 border-[#0D9488] bg-white px-6 py-3 font-heading font-bold text-[#0D9488] hover:bg-[#0D9488]/10">
              <FileText size={16} /> Pay by purchase order
            </button>
          </div>
        </div>
      )}

      {active && !school && (
        <div className="mt-6 rounded-3xl border border-[#E5DECF] bg-white p-6" data-testid="create-school-card">
          <h2 className="font-heading text-lg font-bold text-[#1F2937]">Name your school</h2>
          <p className="font-body text-sm text-[#6B7280]">This is what your teachers and families will see.</p>
          <div className="mt-3 flex gap-2">
            <input value={schoolName} onChange={(e) => setSchoolName(e.target.value)} data-testid="school-name-input" className={inputClass} placeholder="e.g. Maple Elementary" />
            <button onClick={createSchool} disabled={creating} data-testid="create-school-btn" className="inline-flex items-center gap-2 rounded-2xl bg-[#0D9488] px-5 py-3 font-bold text-white hover:bg-[#0B7F74] disabled:opacity-50">
              {creating ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Create
            </button>
          </div>
        </div>
      )}

      {school && (
        <>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            <Stat label="Teacher seats" value={`${school.seats_used} / ${school.seats}`} icon={Ticket} />
            <Stat label="Teachers joined" value={data.teachers.length} icon={GraduationCap} />
            <Stat label="Seats available" value={school.seats_available} icon={Users} />
          </div>
          {school.seats > 0 && (
            <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-[#E5DECF]" data-testid="school-seat-meter">
              <div className="h-full rounded-full bg-[#0D9488]" style={{ width: `${Math.min(100, Math.round((school.seats_used / school.seats) * 100))}%` }} />
            </div>
          )}

          <div className="mt-6 rounded-3xl border border-[#E5DECF] bg-white p-6">
            <div className="flex flex-wrap items-center gap-2">
              <Mail size={18} className="text-[#0D9488]" />
              <h2 className="font-heading text-lg font-bold text-[#1F2937]">Invite teachers</h2>
              <div className="ml-auto inline-flex rounded-full border border-[#E5DECF] bg-[#FAF7F2] p-1" data-testid="invite-mode-toggle">
                {[["single", Mail, "Single"], ["bulk", Users2, "Paste list"]].map(([id, Icon, label]) => (
                  <button key={id} onClick={() => { setMode(id); setBulkResult(null); }} data-testid={`invite-mode-${id}`}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-all ${mode === id ? "bg-white shadow text-[#1F2937]" : "text-[#6B7280] hover:text-[#1F2937]"}`}>
                    <Icon size={12} /> {label}
                  </button>
                ))}
              </div>
            </div>

            {mode === "single" ? (
              <>
                <p className="mt-2 font-body text-sm text-[#6B7280]">Enter an email to invite (sends if your SMTP is set), or leave blank to create a shareable link.</p>
                <div className="mt-3 flex gap-2">
                  <input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} data-testid="invite-email-input" className={inputClass} placeholder="teacher@email.com (optional)" />
                  <button onClick={invite} disabled={inviting || school.seats_available <= 0} data-testid="send-invite-btn" className="inline-flex items-center gap-2 rounded-2xl bg-[#10B981] px-5 py-3 font-bold text-white hover:bg-[#059669] disabled:opacity-50">
                    {inviting ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Invite
                  </button>
                </div>
                {school.seats_available <= 0 && <p className="mt-2 font-body text-sm font-semibold text-[#B45309]">All seats used — revoke a pending invite or upgrade your plan.</p>}
              </>
            ) : (
              <>
                <p className="mt-2 font-body text-sm text-[#6B7280]">Paste a full list of teacher emails — comma, space or newline separated. We'll skip anyone who's already been invited or already teaches here.</p>
                <textarea value={bulkText} onChange={(e) => setBulkText(e.target.value)} data-testid="bulk-invite-textarea"
                  className={`${inputClass} h-32 font-mono text-sm`}
                  placeholder={"ana@school.com\nben@school.com, carol@school.com"} />
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <div className="text-xs font-semibold text-[#6B7280]" data-testid="bulk-parsed-count">
                    {parsedBulk.length} valid email{parsedBulk.length === 1 ? "" : "s"} · {Math.min(parsedBulk.length, school.seats_available)} will use a seat ({school.seats_available} seat{school.seats_available === 1 ? "" : "s"} available)
                  </div>
                  <div className="ml-auto flex gap-2">
                    {bulkText && (
                      <button onClick={clearBulk} data-testid="bulk-clear-btn"
                        className="inline-flex items-center gap-1 rounded-full border-2 border-[#E5DECF] bg-white px-3 py-1.5 text-xs font-bold text-[#1F2937] hover:border-[#1F2937]">
                        Clear
                      </button>
                    )}
                    <button onClick={runBulk} disabled={bulkBusy || parsedBulk.length === 0 || school.seats_available <= 0}
                      data-testid="bulk-invite-btn"
                      className="inline-flex items-center gap-2 rounded-full bg-[#10B981] px-5 py-2 font-bold text-white hover:bg-[#059669] disabled:opacity-50">
                      {bulkBusy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send {parsedBulk.length ? Math.min(parsedBulk.length, school.seats_available) : ""} invite{parsedBulk.length === 1 ? "" : "s"}
                    </button>
                  </div>
                </div>

                {bulkResult && (
                  <div className="mt-4 space-y-2" data-testid="bulk-result">
                    {bulkResult.created.length > 0 && (
                      <div className="rounded-2xl border-2 border-[#10B981]/40 bg-[#10B981]/10 p-3">
                        <div className="flex items-center gap-2 text-sm font-bold text-[#047857]"><CheckCircle2 size={16} /> Invited {bulkResult.created.length}</div>
                        <div className="mt-1 font-body text-xs text-[#047857]/90">{bulkResult.created.map((c) => c.email).join(", ")}</div>
                        {bulkResult.share_links.length > 0 && (
                          <div className="mt-2 text-xs text-[#6B7280]">
                            SMTP not configured yet — <button onClick={() => copyToClipboard(bulkResult.share_links.map((s) => `${s.email}: ${s.link}`).join("\n"), "All links copied")}
                              data-testid="bulk-copy-links" className="font-bold text-[#0D9488] underline">copy all share links</button> and send them yourself.
                          </div>
                        )}
                      </div>
                    )}
                    {bulkResult.skipped.length > 0 && (
                      <div className="rounded-2xl border-2 border-[#F59E0B]/40 bg-[#F59E0B]/10 p-3" data-testid="bulk-skipped">
                        <div className="flex items-center gap-2 text-sm font-bold text-[#B45309]"><AlertTriangle size={16} /> Skipped {bulkResult.skipped.length}</div>
                        <ul className="mt-1 space-y-0.5 font-body text-xs text-[#B45309]/90">
                          {bulkResult.skipped.map((s) => (<li key={s.email}>{s.email} — {s.reason}</li>))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {data.invites.length > 0 && (
              <div className="mt-4">
                <div className="text-xs font-bold uppercase tracking-wide text-[#94A3B8]">Pending invites</div>
                <div className="mt-2 space-y-2">
                  {data.invites.map((i) => (
                    <div key={i.id} className="rounded-2xl border border-[#E5DECF] bg-[#FAF7F2] px-4 py-2.5" data-testid={`invite-row-${i.id}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-body text-sm text-[#1F2937]">{i.email || "Shareable link"}</span>
                        <div className="flex items-center gap-2">
                          <button onClick={() => copyLink(i.token)} data-testid={`copy-invite-${i.id}`} className="text-[#0D9488] hover:opacity-70" aria-label="Copy link"><Copy size={15} /></button>
                          <button onClick={() => revoke(i.id)} data-testid={`revoke-invite-${i.id}`} className="text-[#E05A47] hover:opacity-70" aria-label="Revoke"><Trash2 size={15} /></button>
                        </div>
                      </div>
                      <input readOnly value={inviteLink(i.token)} data-testid={`invite-link-${i.id}`} onFocus={(e) => e.target.select()} className="mt-2 w-full select-all rounded-xl border border-[#E5DECF] bg-white px-3 py-1.5 font-mono text-xs text-[#6B7280] outline-none" />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {data.teachers.length > 0 && (
            <div className="mt-6 rounded-3xl border border-[#E5DECF] bg-white p-6">
              <h2 className="font-heading text-lg font-bold text-[#1F2937]">Teachers</h2>
              <div className="mt-3 space-y-2">
                {data.teachers.map((t) => (
                  <div key={t.id} className="flex items-center justify-between rounded-2xl border border-[#E5DECF] px-4 py-2.5" data-testid={`teacher-row-${t.id}`}>
                    <div><span className="font-heading font-bold text-[#1F2937]">{t.name}</span> <span className="font-body text-sm text-[#6B7280]">· {t.email}</span></div>
                    <span className="font-body text-xs text-[#94A3B8]">{t.students} students</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6"><SmtpCard /></div>

          {/* Need more seats? Pay by purchase order */}
          <div className="mt-6 rounded-3xl border border-dashed border-[#7A3FF2]/40 bg-[#7A3FF2]/5 p-5" data-testid="school-po-upsell">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#7A3FF2]/10 text-[#7A3FF2]"><FileText size={18} /></div>
              <div className="flex-1">
                <div className="font-heading font-bold text-[#1F2937]">Need more teacher seats — or paying by invoice?</div>
                <p className="mt-1 font-body text-sm text-[#6B7280]">Submit a purchase order and our billing team will invoice your district (ACH, wire or check).</p>
              </div>
              <button onClick={() => setShowPO(true)} data-testid="school-po-btn-inline" className="inline-flex items-center gap-2 rounded-full bg-[#7A3FF2] px-5 py-2.5 font-heading font-bold text-white hover:bg-[#652BDB]">
                Submit PO
              </button>
            </div>
          </div>
        </>
      )}
      <PurchaseOrderModal open={showPO} onClose={() => setShowPO(false)} defaultOrg={school?.name || ""} onSubmitted={load} />
      <ScreenTips screenKey="school-home" steps={COACHMARKS["school-home"]} />
    </div>
  );
}
